<template>
  <generic-modal :title="$t('details')" ref="generic_modal" size="fullscreen">
    <div v-if="request && Object.keys(request).length > 0">
      <!-- Header Information -->
      <div class="box">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <div>
                <p class="heading">{{ $t("profile") }}</p>
                <p class="title is-5">
                  <span class="icon">
                    <i class="fa fa-user-circle"></i>
                  </span>
                  {{ request.profile?.name || "N/A" }}
                </p>
              </div>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div>
                <p class="heading">{{ $t("status") }}</p>
                <span
                  class="tag is-large"
                  :class="get_status_class(request.status)"
                >
                  <span class="icon">
                    <i class="fa" :class="get_status_icon(request.status)"></i>
                  </span>
                  <span>{{ translate_status(request.status) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="level">
          <!-- <div class="level-left">
            <div class="level-item">
              <div>
                <p class="heading">{{ $t("request id") }}</p>
                <p class="subtitle is-6">
                  <span class="icon">
                    <i class="fa fa-id-card"></i>
                  </span>
                  {{ request.uuid }}
                </p>
              </div>
            </div>
          </div> -->
          <div class="level-left">
            <div class="level-item">
              <div>
                <p class="heading">{{ $t("created date") }}</p>
                <p class="subtitle is-6">
                  <span class="icon">
                    <i class="fa fa-calendar"></i>
                  </span>
                  {{ format_date_time(request.create_date) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Applicant Request Information -->
      <div class="box" v-if="request.applicant_request">
        <h4 class="title is-5">
          <span class="icon">
            <i class="fa fa-user-plus"></i>
          </span>
          {{ $t("applicant request") }}
        </h4>

        <div class="columns">
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("first name") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold">
                  {{ request.applicant_request.first_name || "N/A" }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("last name") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold">
                  {{ request.applicant_request.last_name || "N/A" }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("email") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold">
                  {{ request.applicant_request.email || "N/A" }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label is-small">{{ $t("phone number") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold">
                  {{ request.applicant_request.phone_number || "N/A" }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-6" v-if="request.applicant_request.location">
            <div class="field">
              <label class="label is-small">{{ $t("ip address") }}</label>
              <div class="control">
                <span class="tag is-dark">
                  <span class="icon">
                    <i class="fa fa-globe"></i>
                  </span>
                  <span>{{
                    request.applicant_request.location.ip_address
                  }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Applicant Response Information -->
      <div class="box" v-if="request.applicant_response">
        <h4 class="title is-5">
          <span class="icon">
            <i class="fa fa-shield"></i>
          </span>
          {{ $t("applicant response") }}
        </h4>

        <div class="columns">
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("onfido id") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold is-size-7">
                  {{ request.applicant_response.id }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("created at") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold">
                  {{ format_date_time(request.applicant_response.created_at) }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("sandbox mode") }}</label>
              <div class="control">
                <span
                  class="tag"
                  :class="
                    request.applicant_response.sandbox
                      ? 'is-warning'
                      : 'is-success'
                  "
                >
                  <span class="icon">
                    <i
                      class="fa"
                      :class="
                        request.applicant_response.sandbox
                          ? 'fa-warning'
                          : 'fa-check'
                      "
                    ></i>
                  </span>
                  <span>{{
                    request.applicant_response.sandbox ? $t("yes") : $t("no")
                  }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns" v-if="request.applicant_response.location">
          <div class="column is-6">
            <div class="field">
              <label class="label is-small">{{ $t("ip address") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold">
                  {{ request.applicant_response.location.ip_address || "N/A" }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-6">
            <div class="field">
              <label class="label is-small">{{
                $t("country of residence")
              }}</label>
              <div class="control">
                <span class="tag is-info">
                  <span class="icon">
                    <i class="fa fa-flag"></i>
                  </span>
                  <span>{{
                    request.applicant_response.location.country_of_residence ||
                    "N/A"
                  }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Onfido Check Information -->
      <div class="box" v-if="request.check_response">
        <h4 class="title is-5">
          <span class="icon">
            <i class="fa fa-check-circle-o"></i>
          </span>
          {{ $t("check response") }}
        </h4>

        <div class="columns">
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("check id") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold is-size-7">
                  {{ request.check_response.id }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("check status") }}</label>
              <div class="control">
                <span
                  class="tag"
                  :class="get_check_status_class(request.check_response.status)"
                >
                  <span class="icon">
                    <i
                      class="fa"
                      :class="
                        get_check_status_icon(request.check_response.status)
                      "
                    ></i>
                  </span>
                  <span>{{
                    format_check_status(request.check_response.status)
                  }}</span>
                </span>
                <!-- <div class="button is-small" @click="refresh_check">
                  <span class="icon">
                    <i class="fa fa-refresh" title="refresh"></i>
                  </span>
                </div> -->
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("check result") }}</label>
              <div class="control">
                <span v-if="request.check_response.result" class="tag is-info">
                  {{ request.check_response.result }}
                </span>
                <span v-else class="tag is-light">{{ $t("pending") }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("check created at") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold">
                  {{ format_date_time(request.check_response.created_at) }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("api version") }}</label>
              <div class="control">
                <span class="tag is-primary"
                  >v{{ request.check_response.version }}</span
                >
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="field">
              <label class="label is-small">{{ $t("paused") }}</label>
              <div class="control">
                <span
                  class="tag"
                  :class="
                    request.check_response.paused ? 'is-warning' : 'is-success'
                  "
                >
                  <span class="icon">
                    <i
                      class="fa"
                      :class="
                        request.check_response.paused ? 'fa-pause' : 'fa-play'
                      "
                    ></i>
                  </span>
                  <span>{{
                    request.check_response.paused ? $t("yes") : $t("no")
                  }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="columns"
          v-if="
            request.check_response.report_ids &&
            request.check_response.report_ids.length > 0
          "
        >
          <div class="column is-12">
            <div class="field">
              <label class="label is-small">{{ $t("report ids") }}</label>
              <div class="control">
                <div class="tags">
                  <span
                    v-for="reportId in request.check_response.report_ids"
                    :key="reportId"
                    class="tag is-dark is-small"
                  >
                    <span class="icon">
                      <i class="fa fa-file-text-o"></i>
                    </span>
                    <span>{{ reportId }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns" v-if="request.check_response.results_uri">
          <div class="column is-12">
            <div class="field">
              <label class="label is-small">{{
                $t("results dashboard")
              }}</label>
              <div class="control">
                <a
                  :href="request.check_response.results_uri"
                  target="_blank"
                  class="button is-link is-small"
                >
                  <span class="icon">
                    <i class="fa fa-external-link"></i>
                  </span>
                  <span>{{ $t("view in onfido dashboard") }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SDK Response - Documents -->
      <div class="box" v-if="request.sdk_response">
        <h4 class="title is-5">
          <span class="icon">
            <i class="fa fa-file-text"></i>
          </span>
          {{ $t("document verification") }}
        </h4>

        <div class="columns">
          <!-- Document Front -->
          <div class="column is-4" v-if="request.sdk_response.document_front">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  <span class="icon">
                    <i class="fa fa-id-card"></i>
                  </span>
                  {{ $t("document front") }}
                </p>
              </div>
              <div class="card-content">
                <div class="field">
                  <label class="label is-small">{{ $t("document id") }}</label>
                  <p class="is-size-7">
                    {{ request.sdk_response.document_front.id }}
                  </p>
                </div>
                <div class="field">
                  <label class="label is-small">{{
                    $t("document type")
                  }}</label>
                  <span class="tag is-primary">{{
                    format_document_type(
                      request.sdk_response.document_front.type
                    )
                  }}</span>
                </div>
                <div class="field">
                  <label class="label is-small">{{ $t("side") }}</label>
                  <span class="tag is-info">{{
                    $t(request.sdk_response.document_front.side)
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Document Back -->
          <div class="column is-4" v-if="request.sdk_response.document_back">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  <span class="icon">
                    <i class="fa fa-id-card"></i>
                  </span>
                  {{ $t("document back") }}
                </p>
              </div>
              <div class="card-content">
                <div class="field">
                  <label class="label is-small">{{ $t("document id") }}</label>
                  <p class="is-size-7">
                    {{ request.sdk_response.document_back.id }}
                  </p>
                </div>
                <div class="field">
                  <label class="label is-small">{{
                    $t("document type")
                  }}</label>
                  <span class="tag is-primary">{{
                    format_document_type(
                      request.sdk_response.document_back.type
                    )
                  }}</span>
                </div>
                <div class="field">
                  <label class="label is-small">{{ $t("side") }}</label>
                  <span class="tag is-info">{{
                    $t(request.sdk_response.document_back.side)
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Face Verification -->
          <div class="column is-4" v-if="request.sdk_response.face">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  <span class="icon">
                    <i class="fa fa-user-circle"></i>
                  </span>
                  {{ $t("face verification") }}
                </p>
              </div>
              <div class="card-content">
                <div class="field">
                  <label class="label is-small">{{ $t("face id") }}</label>
                  <p class="is-size-7">
                    {{ request.sdk_response.face.id }}
                  </p>
                </div>
                <div class="field">
                  <label class="label is-small">{{ $t("variant") }}</label>
                  <span class="tag is-success">{{
                    $t(request.sdk_response.face.variant)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Check Request Information -->
      <div class="box" v-if="request.check_request">
        <h4 class="title is-5">
          <span class="icon">
            <i class="fa fa-cog"></i>
          </span>
          {{ $t("check request") }}
        </h4>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label is-small">{{ $t("applicant id") }}</label>
              <div class="control">
                <p class="has-text-weight-semibold is-size-7">
                  {{ request.check_request.applicant_id }}
                </p>
              </div>
            </div>
          </div>
          <div class="column is-6">
            <div class="field">
              <label class="label is-small">{{ $t("report names") }}</label>
              <div class="control">
                <div class="tags">
                  <span
                    v-for="reportName in request.check_request.report_names"
                    :key="reportName"
                    class="tag is-info is-small"
                  >
                    <span class="icon">
                      <i class="fa fa-file-text-o"></i>
                    </span>
                    <span>{{ reportName }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Check Results - Detailed Verification Reports -->
      <div
        class="box"
        v-if="request.check_result && request.check_result.length > 0"
      >
        <h4 class="title is-5">
          <span class="icon">
            <i class="fa fa-file-text-o"></i>
          </span>
          {{ $t("verification reports") }}
        </h4>

        <div
          v-for="(report, index) in request.check_result"
          :key="report.id"
          class="mb-5"
        >
          <div class="card">
            <div class="card-header">
              <p class="card-header-title">
                <span class="icon">
                  <i class="fa fa-certificate"></i>
                </span>
                {{ format_report_name(report.name) }} - {{ $t("report") }}
              </p>
              <div class="card-header-icon">
                <span class="tag" :class="get_result_class(report.result)">
                  <span class="icon">
                    <i class="fa" :class="get_result_icon(report.result)"></i>
                  </span>
                  <span>{{ format_result(report.result) }}</span>
                </span>
              </div>
            </div>
            <div class="card-content">
              <!-- Report Basic Info -->
              <div class="columns">
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-small">{{ $t("report id") }}</label>
                    <p class="is-size-7">{{ report.id }}</p>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-small">{{ $t("status") }}</label>
                    <span
                      class="tag"
                      :class="get_status_class_report(report.status)"
                    >
                      {{ format_check_status(report.status) }}
                    </span>
                  </div>
                </div>
                <div class="column is-3">
                  <div class="field">
                    <label class="label is-small">{{ $t("created at") }}</label>
                    <p class="has-text-weight-semibold">
                      {{ format_date_time(report.created_at) }}
                    </p>
                  </div>
                </div>
                <div class="column is-3" v-if="report.sub_result">
                  <div class="field">
                    <label class="label is-small">{{ $t("sub result") }}</label>
                    <span
                      class="tag"
                      :class="get_result_class(report.sub_result)"
                    >
                      {{ format_result(report.sub_result) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Document Report Properties -->
              <div
                v-if="
                  report.properties && Object.keys(report.properties).length > 0
                "
                class="mt-4"
              >
                <h6 class="subtitle is-6">
                  <span class="icon">
                    <i class="fa fa-info-circle"></i>
                  </span>
                  {{ $t("extracted information") }}
                </h6>
                <div class="columns is-multiline">
                  <div class="column is-4" v-if="report.properties.first_name">
                    <div class="field">
                      <label class="label is-small">{{
                        $t("first name")
                      }}</label>
                      <p class="has-text-weight-semibold">
                        {{ report.properties.first_name }}
                      </p>
                    </div>
                  </div>
                  <div class="column is-4" v-if="report.properties.last_name">
                    <div class="field">
                      <label class="label is-small">{{
                        $t("last name")
                      }}</label>
                      <p class="has-text-weight-semibold">
                        {{ report.properties.last_name }}
                      </p>
                    </div>
                  </div>
                  <div
                    class="column is-4"
                    v-if="report.properties.date_of_birth"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("date of birth")
                      }}</label>
                      <p class="has-text-weight-semibold">
                        {{ report.properties.date_of_birth }}
                      </p>
                    </div>
                  </div>
                  <div class="column is-4" v-if="report.properties.gender">
                    <div class="field">
                      <label class="label is-small">{{ $t("gender") }}</label>
                      <span class="tag is-info">{{
                        report.properties.gender
                      }}</span>
                    </div>
                  </div>
                  <div class="column is-4" v-if="report.properties.nationality">
                    <div class="field">
                      <label class="label is-small">{{
                        $t("nationality")
                      }}</label>
                      <span class="tag is-success">{{
                        report.properties.nationality
                      }}</span>
                    </div>
                  </div>
                  <div
                    class="column is-4"
                    v-if="report.properties.document_type"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("document type")
                      }}</label>
                      <span class="tag is-primary">{{
                        format_document_type(report.properties.document_type)
                      }}</span>
                    </div>
                  </div>
                  <div
                    class="column is-6"
                    v-if="report.properties.date_of_expiry"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("expiry date")
                      }}</label>
                      <p class="has-text-weight-semibold">
                        {{ report.properties.date_of_expiry }}
                      </p>
                    </div>
                  </div>
                  <div
                    class="column is-6"
                    v-if="report.properties.issuing_country"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("issuing country")
                      }}</label>
                      <span class="tag is-warning">{{
                        report.properties.issuing_country
                      }}</span>
                    </div>
                  </div>
                  <div
                    class="column is-12"
                    v-if="
                      report.properties.document_numbers &&
                      report.properties.document_numbers.length > 0
                    "
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("document numbers")
                      }}</label>
                      <div class="tags">
                        <span
                          v-for="docNum in report.properties.document_numbers"
                          :key="docNum.type"
                          class="tag is-dark"
                        >
                          {{ docNum.type }}: {{ docNum.value }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Facial Similarity Score -->
              <div
                v-if="
                  report.name === 'facial_similarity_photo' &&
                  report.breakdown?.face_comparison?.breakdown?.face_match
                "
                class="mt-4"
              >
                <h6 class="subtitle is-6">
                  <span class="icon">
                    <i class="fa fa-user-circle"></i>
                  </span>
                  {{ $t("face comparison") }}
                </h6>
                <div class="columns">
                  <div class="column is-4">
                    <div class="field">
                      <label class="label is-small">{{
                        $t("similarity score")
                      }}</label>
                      <div class="control">
                        <span
                          class="tag is-large"
                          :class="
                            get_score_class(
                              report.breakdown.face_comparison.breakdown
                                .face_match.properties.score
                            )
                          "
                        >
                          <span class="icon">
                            <i class="fa fa-percent"></i>
                          </span>
                          <span
                            >{{
                              (
                                report.breakdown.face_comparison.breakdown
                                  .face_match.properties.score * 100
                              ).toFixed(2)
                            }}%</span
                          >
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-4">
                    <div class="field">
                      <label class="label is-small">{{
                        $t("match result")
                      }}</label>
                      <span
                        class="tag"
                        :class="
                          get_result_class(
                            report.breakdown.face_comparison.breakdown
                              .face_match.result
                          )
                        "
                      >
                        <span class="icon">
                          <i
                            class="fa"
                            :class="
                              get_result_icon(
                                report.breakdown.face_comparison.breakdown
                                  .face_match.result
                              )
                            "
                          ></i>
                        </span>
                        <span>{{
                          format_result(
                            report.breakdown.face_comparison.breakdown
                              .face_match.result
                          )
                        }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Breakdown Summary for Document Reports -->
              <div
                v-if="report.breakdown && report.name === 'document'"
                class="mt-4"
              >
                <h6 class="subtitle is-6">
                  <span class="icon">
                    <i class="fa fa-check-square-o"></i>
                  </span>
                  {{ $t("verification breakdown") }}
                </h6>
                <div class="columns is-multiline">
                  <div
                    class="column is-4"
                    v-if="report.breakdown.data_validation"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("data validation")
                      }}</label>
                      <span
                        class="tag"
                        :class="
                          get_result_class(
                            report.breakdown.data_validation.result
                          )
                        "
                      >
                        <span class="icon">
                          <i
                            class="fa"
                            :class="
                              get_result_icon(
                                report.breakdown.data_validation.result
                              )
                            "
                          ></i>
                        </span>
                        <span>{{
                          format_result(report.breakdown.data_validation.result)
                        }}</span>
                      </span>
                    </div>
                  </div>
                  <div
                    class="column is-4"
                    v-if="report.breakdown.visual_authenticity"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("visual authenticity")
                      }}</label>
                      <span
                        class="tag"
                        :class="
                          get_result_class(
                            report.breakdown.visual_authenticity.result
                          )
                        "
                      >
                        <span class="icon">
                          <i
                            class="fa"
                            :class="
                              get_result_icon(
                                report.breakdown.visual_authenticity.result
                              )
                            "
                          ></i>
                        </span>
                        <span>{{
                          format_result(
                            report.breakdown.visual_authenticity.result
                          )
                        }}</span>
                      </span>
                    </div>
                  </div>
                  <div
                    class="column is-4"
                    v-if="report.breakdown.image_integrity"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("image integrity")
                      }}</label>
                      <span
                        class="tag"
                        :class="
                          get_result_class(
                            report.breakdown.image_integrity.result
                          )
                        "
                      >
                        <span class="icon">
                          <i
                            class="fa"
                            :class="
                              get_result_icon(
                                report.breakdown.image_integrity.result
                              )
                            "
                          ></i>
                        </span>
                        <span>{{
                          format_result(report.breakdown.image_integrity.result)
                        }}</span>
                      </span>
                    </div>
                  </div>
                  <div
                    class="column is-4"
                    v-if="report.breakdown.data_consistency"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("data consistency")
                      }}</label>
                      <span
                        class="tag"
                        :class="
                          get_result_class(
                            report.breakdown.data_consistency.result
                          )
                        "
                      >
                        <span class="icon">
                          <i
                            class="fa"
                            :class="
                              get_result_icon(
                                report.breakdown.data_consistency.result
                              )
                            "
                          ></i>
                        </span>
                        <span>{{
                          format_result(
                            report.breakdown.data_consistency.result
                          )
                        }}</span>
                      </span>
                    </div>
                  </div>
                  <div
                    class="column is-4"
                    v-if="report.breakdown.compromised_document"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("compromised document")
                      }}</label>
                      <span
                        class="tag"
                        :class="
                          get_result_class(
                            report.breakdown.compromised_document.result
                          )
                        "
                      >
                        <span class="icon">
                          <i
                            class="fa"
                            :class="
                              get_result_icon(
                                report.breakdown.compromised_document.result
                              )
                            "
                          ></i>
                        </span>
                        <span>{{
                          format_result(
                            report.breakdown.compromised_document.result
                          )
                        }}</span>
                      </span>
                    </div>
                  </div>
                  <div
                    class="column is-4"
                    v-if="report.breakdown.age_validation"
                  >
                    <div class="field">
                      <label class="label is-small">{{
                        $t("age validation")
                      }}</label>
                      <span
                        class="tag"
                        :class="
                          get_result_class(
                            report.breakdown.age_validation.result
                          )
                        "
                      >
                        <span class="icon">
                          <i
                            class="fa"
                            :class="
                              get_result_icon(
                                report.breakdown.age_validation.result
                              )
                            "
                          ></i>
                        </span>
                        <span>{{
                          format_result(report.breakdown.age_validation.result)
                        }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>
    <template v-slot:footer>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon">
          <i class="fa fa-close"></i>
        </span>
        <span>{{ $t("close") }}</span>
      </button>
      <button class="button is-primary is-rounded" @click="refresh_check">
        <span class="icon">
          <i class="fa fa-refresh"></i>
        </span>
        <span>{{ $t("refresh onfido result") }}</span>
      </button>
    </template>
  </generic-modal>
</template>
<script>
import {
  put,
  post,
  change_active_tab,
  generate_error_message_text,
  get,
  format_date_time,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";

export default {
  name: "OnfidoRequestDetailModal",
  components: { GenericModal },
  props: {},
  data() {
    return {
      request: {},
      error: "",
    };
  },
  computed: {},
  methods: {
    change_active_tab: change_active_tab,
    format_date_time,
    close_modal() {
      let v = this;
      v.set_initial_state();
      v.$refs.generic_modal.close_modal();
    },
    show_modal(uuid) {
      let v = this;
      v.set_initial_state();
      v.$refs.generic_modal.show_modal();
      v.load_request(uuid);
    },
    load_request(uuid) {
      let v = this;
      v.$root.loader(true);
      get("/onfido/request", { uuid: uuid })
        .then((res) => {
          v.request = res;
        })
        .catch((err) => {
          v.error = generate_error_message_text(err);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    set_initial_state() {
      let v = this;
      v.request = {};
      v.error = "";
    },
    translate_status(status) {
      let v = this;
      switch (status) {
        case "new":
          return v.$t("new");
        case "error":
          return v.$t("error");
        case "applicant_created":
          return v.$t("applicant created");
        case "token_created":
          return v.$t("token created");
        case "check_created":
          return v.$t("check created");
        case "sdk_data_received":
          return v.$t("sdk data received");
        case "check_verified":
          return v.$t("check verified");
        // Legacy status codes
        case "C":
          return v.$t("created");
        case "P":
          return v.$t("processed");
        case "E":
          return v.$t("errored");
        case "S":
          return v.$t("success");
        default:
          return status;
      }
    },
    get_status_class(status) {
      switch (status) {
        case "new":
          return "is-info";
        case "error":
          return "is-danger";
        case "applicant_created":
          return "is-primary";
        case "token_created":
          return "is-link";
        case "check_created":
          return "is-warning";
        case "sdk_data_received":
          return "is-info";
        case "check_verified":
          return "is-success";
        // Legacy status codes
        case "S":
          return "is-success";
        case "P":
          return "is-warning";
        case "E":
          return "is-danger";
        case "C":
          return "is-info";
        default:
          return "is-light";
      }
    },
    get_status_icon(status) {
      switch (status) {
        case "new":
          return "fa-file-o";
        case "error":
          return "fa-exclamation-circle";
        case "applicant_created":
          return "fa-user-plus";
        case "token_created":
          return "fa-key";
        case "check_created":
          return "fa-cogs";
        case "sdk_data_received":
          return "fa-upload";
        case "check_verified":
          return "fa-shield";
        // Legacy status codes
        case "S":
          return "fa-check-circle";
        case "P":
          return "fa-clock-o";
        case "E":
          return "fa-exclamation-circle";
        case "C":
          return "fa-plus-circle";
        default:
          return "fa-question-circle";
      }
    },
    format_document_type(type) {
      if (!type) return "N/A";
      return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    get_check_status_class(status) {
      switch (status) {
        case "complete":
          return "is-success";
        case "in_progress":
          return "is-warning";
        case "awaiting_data":
          return "is-info";
        case "failed":
          return "is-danger";
        default:
          return "is-light";
      }
    },
    get_check_status_icon(status) {
      switch (status) {
        case "complete":
          return "fa-check-circle";
        case "in_progress":
          return "fa-spinner fa-spin";
        case "awaiting_data":
          return "fa-clock-o";
        case "failed":
          return "fa-exclamation-circle";
        default:
          return "fa-question-circle";
      }
    },
    format_check_status(status) {
      if (!status) return "N/A";
      return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    format_report_name(name) {
      if (!name) return "N/A";
      return name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    format_result(result) {
      if (!result) return "N/A";
      return result.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    get_result_class(result) {
      switch (result) {
        case "clear":
          return "is-success";
        case "consider":
          return "is-warning";
        case "unidentified":
          return "is-info";
        case "caution":
          return "is-warning";
        case "suspected":
          return "is-danger";
        default:
          return "is-light";
      }
    },
    get_result_icon(result) {
      switch (result) {
        case "clear":
          return "fa-check-circle";
        case "consider":
          return "fa-exclamation-triangle";
        case "unidentified":
          return "fa-question-circle";
        case "caution":
          return "fa-warning";
        case "suspected":
          return "fa-times-circle";
        default:
          return "fa-question-circle";
      }
    },
    get_status_class_report(status) {
      switch (status) {
        case "complete":
          return "is-success";
        case "in_progress":
          return "is-warning";
        case "awaiting_data":
          return "is-info";
        case "failed":
          return "is-danger";
        default:
          return "is-light";
      }
    },
    get_score_class(score) {
      if (score >= 0.8) return "is-success";
      if (score >= 0.6) return "is-warning";
      return "is-danger";
    },
    refresh_check() {
      let v = this;
      if (!v.request || !v.request.uuid) return;
      v.$root.loader(true);
      post("/onfido/request/refresh-check", { uuid: v.request.uuid })
        .then((res) => {
          v.load_request(v.request.uuid);
        })
        .catch((err) => {
          v.error = generate_error_message_text(err);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
  },
  mounted() {},
  created() {},
  watch: {},
};
</script>
<style></style>
